import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getUserProfile,
  updateProfile,
  updateSocialProfile,
  getEducation,
  updateProfileSummary
} from '../../services/profile';
import Education from './Education';
import Experience from './Experience';
import Skills from './Skills';
import Projects from './Projects';
import Certificates from './Certificates';
import ExtraCurricular from './ExtraCurricular';
import CustomSection from './CustomSection';
import ProfilePreview from './ProfilePreview';
import {
  FaSave,
  FaTimes,
  FaSpinner,
  FaLinkedin,
  FaGithub,
  FaGlobe
} from 'react-icons/fa';
import './ProfileForm.css';

const ProfileForm = (props) => {
  const { initialSection, setSection, profileSection, setCompletion } = props;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialSection);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');

  // Profile data state
  const [profileData, setProfileData] = useState({
    // Personal Info
    user: {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      location: '',
    },

    profile_id: '',
    profile_name: '',

    // Optional Fields
    summary: '',

    // Social Links
    linkedin: '',
    github: '',
    website: '',

    // Education
    education: [],

    // Skills
    skill_categories: [],

    // Experience
    experiences: [],

    // Projects
    projects: [],

    // Certificates
    certificates: [],

    // Extra Curricular Activities
    extracurricular_activities: [],

    // Custom Sections
    custom_sections: []
  });

  // Fetch user profile data on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const data = await getUserProfile();
        setProfileData(data);
      } catch (err) {
        setError('Failed to load profile data');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Handle input change for basic fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
    setCompletion('personal', ["done"])
  };

  // Handle input change for user fields
  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      user: {
        ...prev.user,
        [name]: value
      }
    }));
  };

  // Handle input change for social links
  const handleSocialInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save profile changes
  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSaveSuccess(false);

    try {
      // First update the user and summary data
      const profileUpdateData = {
        user: {
          first_name: profileData.user.first_name,
          last_name: profileData.user.last_name,
          phone_number: profileData.user.phone_number,
          location: profileData.user.location
        },
        summary: profileData.summary
      };

      await updateProfile(profileUpdateData);

      // Then update social media profiles
      const socialData = {
        linkedin: profileData.linkedin,
        github: profileData.github,
        website: profileData.website
      };

      await updateSocialProfile(socialData, profileData?.profile_id);

      // Then update social media profiles
      const summaryData = {
        summary: profileData.summary
      };

      await updateProfileSummary(summaryData, profileData?.profile_id);

      setSaveSuccess(true);

      // Reset success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to save profile');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };


  // Render loading state
  if (isLoading) {
    return (
      <div className="loading">
        <FaSpinner className="loading-spinner" />
        <p>Loading your profile...</p>
      </div>
    );
  }

  // Render error state
  if (error && !profileData) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={() => navigate('/dashboard')} className="btn-secondary">
          <FaTimes /> Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="profile-form-container">

      {error && <div className="error-message">{error}</div>}
      {saveSuccess && <div className="success-message">Profile saved successfully!</div>}

      <div className="profile-content">
        {initialSection === 'personal' && (
          <div className="profile-section side-by-side">
            <div className="form-side">
              <h2>
                <div className="section-icon">
                    {profileSection.icon}
                  </div> 
                Personal Information
              </h2>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">First Name</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={profileData?.user?.first_name || ''}
                    onChange={handleUserInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="last_name">Last Name</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={profileData?.user?.last_name || ''}
                    onChange={handleUserInputChange}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profileData?.user?.email || ''}
                  onChange={handleUserInputChange}
                  disabled // Email cannot be changed directly from the profile
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="phone_number">Phone Number</label>
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={profileData?.user?.phone_number || ''}
                  onChange={handleUserInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="location">Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={profileData?.user?.location || ''}
                  onChange={handleUserInputChange}
                  placeholder="City, State, Country"
                />
              </div>
              
              <div className="form-divider"></div>
              
              <h3>Social Links</h3>
              
              <div className="form-group">
                <label htmlFor="linkedin">
                  <FaLinkedin className="input-icon" /> LinkedIn URL
                </label>
                <input
                  type="url"
                  id="linkedin"
                  name="linkedin"
                  value={profileData.linkedin || ''}
                  onChange={handleSocialInputChange}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="github">
                  <FaGithub className="input-icon" /> GitHub URL
                </label>
                <input
                  type="url"
                  id="github"
                  name="github"
                  value={profileData.github || ''}
                  onChange={handleSocialInputChange}
                  placeholder="https://github.com/yourusername"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="website">
                  <FaGlobe className="input-icon" /> Personal Website
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={profileData.website || ''}
                  onChange={handleSocialInputChange}
                  placeholder="https://yourdomain.com"
                />
              </div>
              
              <div className="form-divider"></div>
              
              <div className="form-group">
                <label htmlFor="summary">Professional Summary</label>
                <textarea
                  id="summary"
                  name="summary"
                  value={profileData?.summary || ''}
                  onChange={handleInputChange}
                  rows="6"
                  placeholder="Write a brief summary of your professional background, skills, and career goals..."
                />
                <small className="form-text">
                  A compelling summary will help employers quickly understand your qualifications and objectives.
                </small>
              </div>
            </div>
            
            {/* <div className="preview-side">
              <ProfilePreview profileData={profileData} />
            </div> */}
          </div>
        )}
        
        {initialSection === 'education' && (
          <Education 
            educationList={profileData?.education}
            profileData={profileData}
            setEducationList={(newEducation) => setProfileData(prev => ({
              ...prev,
              education: newEducation
            }))}
            profileSection={profileSection}
            setCompletion={setCompletion}
          />
        )}
        
        {initialSection === 'skills' && (
          <Skills 
            skillsList={profileData?.skill_categories}
            profileData={profileData}
            setSkillsList={(newSkills) => setProfileData(prev => ({
              ...prev,
              skill_categories: newSkills
            }))}
            profileSection={profileSection}
            setCompletion={setCompletion}
          />
        )}
        
        {initialSection === 'experience' && (
          <Experience 
            experienceList={profileData?.experiences}
            profileData={profileData}
            setExperienceList={(newExperience) => setProfileData(prev => ({
              ...prev,
              experiences: newExperience
            }))}
            profileSection={profileSection}
            setCompletion={setCompletion}
          />
        )}
        
        {initialSection === 'projects' && (
          <Projects 
            projectsList={profileData?.projects}
            profileData={profileData}
            setProjectsList={(newProjects) => setProfileData(prev => ({
              ...prev,
              projects: newProjects
            }))}
            profileSection={profileSection}
            setCompletion={setCompletion}
          />
        )}
        
        {initialSection === 'certificates' && (
          <Certificates 
            certificatesList={profileData?.certificates}
            profileData={profileData}
            setCertificatesList={(newCertificates) => setProfileData(prev => ({
              ...prev,
              certificates: newCertificates
            }))}
            profileSection={profileSection}
            setCompletion={setCompletion}
          />
        )}
        
        {initialSection === 'extra' && (
          <ExtraCurricular 
            extraCurricularList={profileData?.extracurricular_activities}
            profileData={profileData}
            setExtraCurricularList={(newExtraCurricular) => setProfileData(prev => ({
              ...prev,
              extracurricular_activities: newExtraCurricular
            }))}
            profileSection={profileSection}
            setCompletion={setCompletion}
          />
        )}
        
        {initialSection === 'custom' && (
          <CustomSection 
            customSectionsList={profileData?.custom_sections}
            profileData={profileData}
            setCustomSectionsList={(newCustomSections) => setProfileData(prev => ({
              ...prev,
              custom_sections: newCustomSections
            }))}
            profileSection={profileSection}
            setCompletion={setCompletion}
          />
        )}
      </div>
      
      <div className="profile-actions">
        <button onClick={() => navigate('/dashboard')} className="btn-secondary">
          <FaTimes /> Cancel
        </button>
        <button
          onClick={handleSave}
          className="btn-primary"
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <FaSpinner className="icon-spin" /> Saving...
            </>
          ) : (
            <>
              <FaSave /> Save Profile
            </>
          )}
        </button>
      </div>
    </div>
      );
};

      export default ProfileForm;